import React from 'react';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  FileTextOutlined,
  HourglassOutlined,
  LinkOutlined,
  MessageOutlined,
  PhoneOutlined,
  QuestionOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from '@ant-design/icons';

import { CONTACT_EMAIL, CONTACT_PHONE } from '../../../constants/business';

export const PAYMENT_LINK_STATE = {
  FEE_PENDING: 'feePending',
  PAID: 'paid',
  CANCELLED: 'cancelled',
  NOT_RECOGNISED: 'notRecognised',
};

export const PAYMENT_LINK_STATE_CONTENT = {
  [PAYMENT_LINK_STATE.FEE_PENDING]: {
    tone: 'pending',
    heroIcon: <HourglassOutlined />,
    title: 'We’re working out the cost of your appointment',
    subtitle: 'Your booking is confirmed. There’s nothing for you to do yet.',
    hasIllustration: true,
    keyTerms: [
      {
        key: 'held',
        icon: <CalendarOutlined />,
        content: (
          <>
            <b>Your appointment is held.</b> You don’t need to call us or book
            again.
          </>
        ),
      },
      {
        key: 'checkBack',
        icon: <ReloadOutlined />,
        content: (
          <>
            <b>Check back in a day or so</b> — reopen this link and the amount
            will be here.
          </>
        ),
      },
      {
        key: 'link',
        icon: <LinkOutlined />,
        content: (
          <>
            <b>This link keeps working.</b> Reopen this page any time — once the
            amount is confirmed you’ll be able to pay here.
          </>
        ),
      },
      {
        key: 'due',
        icon: <ClockCircleOutlined />,
        content: (
          <>
            <b>Please pay at least 3 business days before your appointment.</b>{' '}
            If we haven’t received it by then, we’ll send you a reminder.
          </>
        ),
      },
      {
        key: 'contact',
        icon: <PhoneOutlined />,
        content: (
          <>
            If something doesn’t look right, call us on <b>{CONTACT_PHONE}</b>{' '}
            and we’ll sort it out with you.
          </>
        ),
      },
    ],
  },
  [PAYMENT_LINK_STATE.PAID]: {
    tone: 'success',
    heroIcon: <CheckOutlined />,
    title: 'You’ve already paid for this appointment',
    subtitle: 'Nothing further is needed from you.',
    hasIllustration: true,
    keyTerms: [
      {
        key: 'receipt',
        icon: <FileTextOutlined />,
        content: (
          <>
            <b>Your receipt is in your email.</b> Sent to the address you booked
            with.
          </>
        ),
      },
      {
        key: 'videoLink',
        icon: <MessageOutlined />,
        content: (
          <>
            <b>Your video link arrives by SMS and email.</b> Sent 3 business
            days and again 30 minutes before your appointment.
          </>
        ),
      },
      {
        key: 'medicare',
        icon: <RollbackOutlined />,
        content: (
          <>
            <b>We lodge your Medicare claim within 3 business days</b> of you
            attending. Medicare pays any rebate straight into your nominated
            bank account.
          </>
        ),
      },
      {
        key: 'contact',
        icon: <PhoneOutlined />,
        content: (
          <>
            Need a copy of your receipt? Call <b>{CONTACT_PHONE}</b> or email{' '}
            <b>{CONTACT_EMAIL}</b>.
          </>
        ),
      },
    ],
  },
  [PAYMENT_LINK_STATE.CANCELLED]: {
    tone: 'warning',
    heroIcon: <CloseOutlined />,
    title: 'This appointment has been cancelled',
    subtitle: 'There’s nothing to pay, and this link is no longer active.',
    hasIllustration: false,
    hasRebookAction: true,
    keyTerms: [
      {
        key: 'refund',
        icon: <RollbackOutlined />,
        content: (
          <>
            <b>If you already paid, we’ll refund you.</b> Refunds go back to the
            card you used, usually within 5 business days. Call us if you
            haven’t seen it.
          </>
        ),
      },
      {
        key: 'rebook',
        icon: <CalendarOutlined />,
        content: (
          <>
            <b>Still want to be seen?</b> You can book a new appointment
            whenever suits you.
          </>
        ),
      },
      {
        key: 'contact',
        icon: <PhoneOutlined />,
        content: (
          <>
            If you didn’t expect this cancellation, call us on{' '}
            <b>{CONTACT_PHONE}</b>.
          </>
        ),
      },
    ],
  },
  [PAYMENT_LINK_STATE.NOT_RECOGNISED]: {
    tone: 'error',
    heroIcon: <QuestionOutlined />,
    title: 'We couldn’t open this payment link',
    subtitle:
      'The link may be incomplete, or it may belong to a different appointment.',
    hasIllustration: false,
    keyTerms: [
      {
        key: 'fullLink',
        icon: <MessageOutlined />,
        content: (
          <>
            <b>Open the full link from your SMS.</b> Links sometimes get cut
            short when they’re copied or forwarded.
          </>
        ),
      },
      {
        key: 'noAppointment',
        icon: <CalendarOutlined />,
        content: (
          <>
            <b>You might not have an appointment booked yet.</b> A payment only
            shows here once your appointment is booked.
          </>
        ),
      },
      {
        key: 'alreadyPaid',
        icon: <CheckCircleOutlined />,
        content: (
          <>
            <b>If you’ve already paid, you’re all set.</b> Nothing further is
            needed from you.
          </>
        ),
      },
      {
        key: 'contact',
        icon: <PhoneOutlined />,
        content: (
          <>
            Still stuck? Call us on <b>{CONTACT_PHONE}</b> and we’ll take the
            payment over the phone or send you a fresh link.
          </>
        ),
      },
    ],
  },
};
